import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { boxShadowMixins } from '@styles/mixins';
import { TILE_FALLBACK_IMAGE } from '@mock/fallback';
import { getImageUrl } from '@util/images';
import { breakpoints } from '@styles/breakpoints';
import { LazyLoadImage } from '@components/common/lazy-load';
import { RippleCard } from '@components/common/ripple-card';
import { mixins } from '@styles/mixins';

/* ***************** *
 *       Types       *
 * ***************** */
export interface GuideTileProps {
  theme?: Theme;
  testId?: string;
  imageUrl: string;
  shortDescription: string;
  title: string;
  sizes?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const TileWrapper = styled(RippleCard)(({ theme }: ThemeProps) => ({
  cursor: 'pointer',
  color: theme.custom.colors.black,
  position: 'relative',

  ['&:hover']: {
    ['p']: {
      color: theme.custom.colors.group2.base
    }
  }
}));

const ImageWrapper = styled.div({
  paddingBottom: '50%',
  position: 'relative'
});

const TileImage: any = styled(LazyLoadImage)(({ theme }: ThemeProps) => ({
  ...mixins(theme).card,
  transition: `background-size ${theme.custom.transitions.smooth}`,
  marginBottom: theme.custom.spacing.medium,
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).light,
  WebkitUserSelect: 'none' /* disable selection/Copy of UIWebView */,
  WebkitTouchCallout: 'none' /* disable the IOS popup when long-press on a link */
}));

const TileTitle = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  fontWeight: 'bold'
}));

const TitleShortDescription = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

/* ************************ *
 *    GuideTileComponent    *
 * ************************ */

export const SimpleTileComponent = ({ title, shortDescription, imageUrl, sizes, testId }: GuideTileProps) => {
  if (!title) return null;

  return (
    <TileWrapper data-testid={testId || TestId.guides.tile}>
      <ImageWrapper>
        <TileImage src={getImageUrl(imageUrl, breakpoints.large) || TILE_FALLBACK_IMAGE} sizes={sizes} />
      </ImageWrapper>
      <TileTitle>{title}</TileTitle>
      <TitleShortDescription>{shortDescription}</TitleShortDescription>
    </TileWrapper>
  );
};

export const SimpleTile = withTheme(SimpleTileComponent);
